import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  Box,
  Tab,
  Tabs,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import { useState } from "react";
import SearchBar from "../../../components/SearchBar";
import CommonBreadcrumb from "../../../components/CommonBreadcrumb";
import SwitchToggle from "../../../components/SwitchToggle";
import AddBrand from "../../../Modals/addBrand";
import DeleteConfirmModal from "../../../Modals/DeleteConfirmModal";
import { useGetBrandsQuery, useDeleteBrandMutation, useUpdateBrandMutation } from "../../../service/features/api";
import Pagination from "../../../components/Panigation";
import { usePermission } from "../../../hooks/usePermission";
import Can from "../../../components/Can";
import { toast } from "react-toastify";
import { VEHICLE_TYPE } from "../../../constants/enum";
import { getVehicleTypeLabel } from "../../../utils/vehicle";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const typeMapping = [
  VEHICLE_TYPE.CAR,
  VEHICLE_TYPE.BIKE,
  VEHICLE_TYPE.BICYCLE,
  VEHICLE_TYPE.CARAVAN,
  VEHICLE_TYPE.TRUCK,
  VEHICLE_TYPE.HEAVY_VEHICLE
];
const tabTypes = typeMapping.map(type => getVehicleTypeLabel(type));

const ManageBrands = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission("Vehicle Specification", "isEdit");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<any>(null);
  const [valueTabs, setValueTabs] = useState(0);

  const { data: brandsData, isFetching } = useGetBrandsQuery({
    search: searchTerm,
    page: page,
    limit: 10,
    type: typeMapping[valueTabs]
  });

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
    setPage(1);
  };

  const [deleteBrand, { isLoading: isDeleting }] = useDeleteBrandMutation();
  const [updateBrand] = useUpdateBrandMutation();

  const brands = brandsData?.data?.data || [];
  const paginator = brandsData?.data?.paginator;

  const handleEdit = (brand: any) => {
    setSelectedBrand(brand);
    setOpenModal(true);
  };

  const handleDeleteClick = (brand: any) => {
    setSelectedBrand(brand);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedBrand?._id) return;
    try {
      await deleteBrand(selectedBrand._id).unwrap();
      toast.success("Brand deleted successfully");
      setOpenDeleteModal(false);
      // If last item on page deleted, go back a page
      if (brands.length === 1 && page > 1) {
        setPage(page - 1);
      }
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete brand");
    }
  };

  const handleToggle = async (brand: any, field: string, value: boolean) => {
    const payload: any = {
      id: brand._id,
      [field]: value
    };

    try {
      await updateBrand(payload).unwrap();
      toast.success("Updated successfully");
    } catch (err: any) {
      toast.error(err?.data?.message || "Update failed");
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedBrand(null);
  };

  return (
    <>
      <CommonBreadcrumb
        title="إدارة العلامات التجارية / Brand Management"
        breadcrumbItems={[
          { label: "لوحة المعلومات / Dashboard", onClick: () => navigate("/dashboard") },
          { label: "المركبات المدرجة / Listed Vehicles", onClick: () => navigate("/manage-vehicles") },
          { label: "إدارة العلامات التجارية / Brand Management" },
        ]}
      />

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              className="custom_tabs_links"
            >
              {tabTypes.map((label, index) => (
                <Tab key={label} label={label} {...a11yProps(index)} />
              ))}
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={(val) => {
                setSearchTerm(val);
                setPage(1);
              }}
              placeholder="البحث في العلامات التجارية... / Search Brands..."
            />
          </div>
          <div className="right_s">
            <Can module="Vehicle Specification" action="isAdd" isDisabledMode={true}>
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setSelectedBrand(null);
                  setOpenModal(true);
                }}
              >
                <AddIcon /> إضافة علامة تجارية / Add Brand
              </Button>
            </Can>
          </div>
        </div>

        {tabTypes.map((type, tabIndex) => (
          <CustomTabPanel key={type} value={valueTabs} index={tabIndex}>
            <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>م / Sr. No.</TableCell>
                <TableCell>تفاصيل العلامة التجارية / Brand Detail</TableCell>
                <TableCell>تعطيل/تمكين / Disable/Enable</TableCell>
                <TableCell>شائع / Popular</TableCell>
                <TableCell>أفضل علامة تجارية / Top Brand</TableCell>
                <TableCell>إجراءات / Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : brands.map((row: any, index: number) => (
                <TableRow hover key={row._id}>
                  <TableCell>{(page - 1) * 10 + index + 1}</TableCell>
                  <TableCell>
                    <div className="user_block">
                      <figure className="no_radius">
                        <img
                          src={row.logo || "/static/images/placeholder.png"}
                          alt={row.name}
                          onError={(e: any) => { e.target.src = "/static/images/placeholder.png" }}
                        />
                      </figure>
                      <p>
                        <b>{row.name || "--"}</b>
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <SwitchToggle
                        checked={row.isActive}
                        onChange={(e) => handleToggle(row, "isActive", e.target.checked)}
                      />
                    ) : (
                      <span className={`status_btn ${row.isActive ? "c_dark_info" : "c_dark_warning"}`}>
                        {row.isActive ? "Enabled" : "Disabled"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <SwitchToggle
                        checked={row.isPopular}
                        onChange={(e) => handleToggle(row, "isPopular", e.target.checked)}
                      />
                    ) : (
                      <span className={`status_btn ${row.isPopular ? "c_dark_info" : "c_dark_warning"}`}>
                        {row.isPopular ? "Popular" : "Normal"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {canEdit ? (
                      <SwitchToggle
                        checked={row.isTopBrand}
                        onChange={(e) => handleToggle(row, "isTopBrand", e.target.checked)}
                      />
                    ) : (
                      <span className={`status_btn ${row.isTopBrand ? "c_dark_info" : "c_dark_warning"}`}>
                        {row.isTopBrand ? "Top Brand" : "Normal"}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="table_actions v2">
                      <Can module="Vehicle Specification" action="isView">
                        <Tooltip title="View" arrow>
                          <IconButton
                            onClick={() => navigate('/manage-brands/detail', { state: { id: row._id } })}
                          >
                            <img src="/static/images/eye_icon.svg" alt="View" />
                          </IconButton>
                        </Tooltip>
                      </Can>
                      <Can module="Vehicle Specification" action="isEdit" isDisabledMode={true}>
                        <Tooltip title="Edit" arrow>
                          <IconButton
                            onClick={() => handleEdit(row)}
                          >
                            <img src="/static/images/edit2_icon.svg" alt="Edit" />
                          </IconButton>
                        </Tooltip>
                      </Can>
                      <Can module="Vehicle Specification" action="isDelete" isDisabledMode={true}>
                        <Tooltip title="Delete" arrow>
                          <IconButton onClick={() => handleDeleteClick(row)}>
                            <img src="/static/images/trash2_icon.svg" alt="Delete" />
                          </IconButton>
                        </Tooltip>
                      </Can>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!isFetching && brands.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">لا توجد علامات تجارية / No Brands Found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {paginator && paginator.pageCount > 1 && (
          <Pagination
            module={brands}
            page={page}
            onPageChange={(p) => setPage(p)}
            totalPages={paginator.pageCount}
            totalCount={paginator.itemCount}
            setPage={setPage}
          />
        )}
          </CustomTabPanel>
        ))}
      </div>

      <AddBrand
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        brandData={selectedBrand}
        defaultType={typeMapping[valueTabs]}
      />

      <DeleteConfirmModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        message={`Are you sure you want to delete the brand "${selectedBrand?.name}"?`}
      />
    </>
  );
};

export default ManageBrands;